import React, { useEffect, useState } from 'react';
import {
    Card,
    CardBody,
    Table,
    Badge,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Spinner,
    Input,
    InputGroup,
    Button,
    ButtonGroup,
    Col,
    Row
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import { getDashboardUsersPaginated } from '../../../../slices/admin/thunk';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { getEntityProfileLink } from '../../../../utils/utils';

const RecentlyAddedEntities = ({ onViewProfile }) => {
    const dispatch = useDispatch();
    const [entities, setEntities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState({});
    const [loadingMore, setLoadingMore] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    // New state for search and sorting
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchTimeout, setSearchTimeout] = useState(null);

    const navigate = useNavigate();
    const pageSize = 10;

    // Toggle dropdown for specific row
    const toggleDropdown = (entityId) => {
        setDropdownOpen(prev => ({
            ...prev,
            [entityId]: !prev[entityId]
        }));
    };

    const handleLoadMore = () => {
        if (!loadingMore && hasMore) {
            fetchEntities(currentPage + 1, true); // append=true
        }
    };

    // Handle search with debouncing
    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchQuery(value);

        // Clear existing timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Set new timeout for debounced search
        const newTimeout = setTimeout(() => {
            resetAndFetch();
        }, 500); // 500ms delay

        setSearchTimeout(newTimeout);
    };

    // Handle sort change
    const handleSortChange = (newSortBy) => {
        let newSortOrder = 'desc';

        // If clicking the same sort field, toggle order
        if (newSortBy === sortBy) {
            newSortOrder = sortOrder === 'desc' ? 'asc' : 'desc';
        }

        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
        resetAndFetch(newSortBy, newSortOrder);
    };

    // Reset pagination and fetch from beginning
    const resetAndFetch = (newSortBy = sortBy, newSortOrder = sortOrder, newQuery = searchQuery) => {
        setCurrentPage(0);
        setEntities([]);
        setHasMore(true);
        fetchEntities(0, false, newSortBy, newSortOrder, newQuery);
    };

    // Clear search
    const clearSearch = () => {
        setSearchQuery('');
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }
        resetAndFetch(sortBy, sortOrder, '');
    };

    // Fetch entities from API
    const fetchEntities = async (
        page = 0,
        append = false,
        currentSortBy = sortBy,
        currentSortOrder = sortOrder,
        currentQuery = searchQuery
    ) => {
        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            const params = {
                page: page,
                sortBy: currentSortBy,
                sortOrder: currentSortOrder,
                pageSize
            };

            // Add search query if present
            if (currentQuery && currentQuery.trim()) {
                params.query = currentQuery.trim();
                params.searchFields = ['email', 'firstName', 'lastName']; // Adjust based on your backend
            }

            const response = await dispatch(getDashboardUsersPaginated(params));

            console.log('Fetched entities:', response);

            if (!response.payload || !response.payload.data) {
                throw new Error('Failed to fetch entities');
            }

            const newEntities = response.payload.data || [];
            const totalDocs = response.payload?.total || 0;
            const totalPages = totalDocs > 0 ? Math.ceil(totalDocs / pageSize) : 1;

            if (append) {
                // Append new entities to existing ones
                setEntities(prevEntities => [...prevEntities, ...newEntities]);
            } else {
                // Replace entities
                setEntities(newEntities);
            }

            setCurrentPage(page);
            setTotalPages(totalPages);
            setHasMore(page < totalPages - 1);

        } catch (error) {
            console.error('Error fetching entities:', error);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        fetchEntities();

        // Cleanup timeout on unmount
        return () => {
            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
        };
    }, []);

    // Get role badge color
    const getRoleBadgeColor = (role) => {
        const roleColors = {
            'admin': 'danger',
            'national': 'primary',
            'ga': 'warning',
            'broker': 'info',
            'employer': 'success',
            'employee': 'secondary',
            'individual': 'dark'
        };
        return roleColors[role?.toLowerCase()] || 'light';
    };

    // Handle view profile action
    const handleViewProfile = (entity) => {
        if (onViewProfile && typeof onViewProfile === 'function') {
            onViewProfile(entity);
        } else {
            console.log('View profile for entity:', entity);
            const link = getEntityProfileLink(entity.id, entity.role);
            console.log('Navigating to profile link:', link);
            if (link) {
                navigate(link);
            }
        }
    };

    const renderUpline = (entity) => {
        const { nationalId, brokerId, gaId } = entity;
        const uplineLinks = [];

        if (nationalId) {
            uplineLinks.push(
                <button
                    key={`national-${nationalId}`}
                    className="btn btn-link p-0 text-start"
                    onClick={() => handleViewProfile({ id: nationalId, role: 'national' })}
                    title="View National profile"
                >
                    <span className="fs-13 fw-medium text-primary">National</span>
                </button>
            );
        }

        if (brokerId) {
            uplineLinks.push(
                <button
                    key={`broker-${brokerId}`}
                    className="btn btn-link p-0 text-start"
                    onClick={() => handleViewProfile({ id: brokerId, role: 'broker' })}
                    title="View Broker profile"
                >
                    <span className="fs-13 fw-medium text-primary">Broker</span>
                </button>
            );
        }

        if (gaId) {
            uplineLinks.push(
                <button
                    key={`ga-${gaId}`}
                    className="btn btn-link p-0 text-start"
                    onClick={() => handleViewProfile({ id: gaId, role: 'ga' })}
                    title="View GA profile"
                >
                    <span className="fs-13 fw-medium text-primary">GA</span>
                </button>
            );
        }

        if (uplineLinks.length === 0) {
            return <span className="text-muted fs-13"></span>;
        }
        return (
            <div className="d-flex flex-column">
                {uplineLinks.map((link, index) => (
                    <span key={index} className="mb-1">
                        {link}
                    </span>
                ))}
            </div>
        );
    };

    // Get sort icon
    const getSortIcon = (field) => {
        if (sortBy !== field) {
            return <i className="ri-sort-asc text-muted ms-1"></i>;
        }
        return sortOrder === 'asc'
            ? <i className="ri-sort-asc text-primary ms-1"></i>
            : <i className="ri-sort-desc text-primary ms-1"></i>;
    };

    return (
        <Card>
            <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">Recently Added Entities</h5>
                    <Badge color="light" className="fs-12">
                        {entities.length} entities
                    </Badge>
                </div>

                {/* Search and Sort Controls */}
                <Row className="mb-3">
                    <Col md={6}>
                        <InputGroup>
                            <Input
                                type="text"
                                placeholder="Search by email..."
                                value={searchQuery}
                                onChange={handleSearchChange}
                                className="form-control-sm"
                            />
                            {searchQuery && (
                                <Button
                                    color="light"
                                    size="sm"
                                    onClick={clearSearch}
                                    title="Clear search"
                                >
                                    <i className="ri-close-line"></i>
                                </Button>
                            )}
                            <Button color="light" size="sm" disabled>
                                <i className="ri-search-line"></i>
                            </Button>
                        </InputGroup>
                    </Col>
                    <Col md={6} className="text-end">
                        <span className="text-muted me-2 fs-13">Sort by:</span>
                        <ButtonGroup size="sm">
                            <Button
                                color={sortBy === 'createdAt' ? 'primary' : 'light'}
                                onClick={() => handleSortChange('createdAt')}
                                className="fs-12"
                            >
                                Date Added
                                {sortBy === 'createdAt' && (
                                    <i className={`ms-1 ${sortOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>
                                )}
                            </Button>
                            <Button
                                color={sortBy === 'lastLogin' ? 'primary' : 'light'}
                                onClick={() => handleSortChange('lastLogin')}
                                className="fs-12"
                            >
                                Last Login
                                {sortBy === 'lastLogin' && (
                                    <i className={`ms-1 ${sortOrder === 'asc' ? 'ri-sort-asc' : 'ri-sort-desc'}`}></i>
                                )}
                            </Button>
                        </ButtonGroup>
                    </Col>
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading entities...</p>
                    </div>
                ) : entities.length === 0 ? (
                    <div className="text-center py-4">
                        <p className="text-muted">
                            {searchQuery ? `No entities found matching "${searchQuery}"` : 'No entities found'}
                        </p>
                        {searchQuery && (
                            <Button color="link" size="sm" onClick={clearSearch}>
                                Clear search and show all
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table className="table-nowrap align-middle table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Role</th>
                                    <th>Invited By</th>
                                    <th>Upline</th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSortChange('lastLogin')}
                                        title="Click to sort by Last Login"
                                    >
                                        Last Login
                                        {getSortIcon('lastLogin')}
                                    </th>
                                    <th
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => handleSortChange('createdAt')}
                                        title="Click to sort by Date Added"
                                    >
                                        Date Added
                                        {getSortIcon('createdAt')}
                                    </th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {entities.map((entity, index) => (
                                    <tr key={entity.id || index}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="flex-shrink-0 me-2">
                                                    <div className="avatar-xs bg-soft-primary rounded-circle d-flex align-items-center justify-content-center">
                                                        <span className="fs-12 fw-semibold text-primary">
                                                            {(entity.firstName?.charAt(0) || '?').toUpperCase()}
                                                            {(entity.lastName?.charAt(0) || '').toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex-grow-1">
                                                    <h6 className="fs-14 mb-0">
                                                        {entity.firstName} {entity.lastName}
                                                    </h6>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="text-muted fs-13">{entity.email}</span>
                                        </td>
                                        <td>
                                            <Badge
                                                color={getRoleBadgeColor(entity.role)}
                                                pill
                                                className="fs-11"
                                            >
                                                {entity.role || 'Unknown'}
                                            </Badge>
                                        </td>
                                        <td>
                                            {entity.invitedByUserInfo ? (
                                                <div className="d-flex align-items-center">
                                                    <div className="flex-shrink-0 me-2">
                                                        <div className="avatar-xxs bg-soft-info rounded-circle d-flex align-items-center justify-content-center">
                                                            <span className="fs-10 fw-semibold text-info">
                                                                {/* Iniciales del invitador */}
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <button
                                                            className="btn btn-link p-0 text-start"
                                                            onClick={() => handleViewProfile(entity.invitedByUserInfo)}
                                                            title="View inviter profile"
                                                        >
                                                            <span className="fs-13 fw-medium text-primary">
                                                                {entity.invitedByUserInfo.firstName} {entity.invitedByUserInfo.lastName}
                                                            </span>
                                                            <br />
                                                            <Badge color={getRoleBadgeColor(entity.invitedByUserInfo.role)}>
                                                                {entity.invitedByUserInfo.role}
                                                            </Badge>
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                ''
                                            )}
                                        </td>
                                        <td>
                                            {renderUpline(entity)}
                                        </td>
                                        <td>
                                            <span className="text-muted fs-13">
                                                {entity.lastLogin
                                                    ? moment.unix(entity.lastLogin).format('MMM DD, YYYY')
                                                    : ''
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            <span className="text-muted fs-13">
                                                {entity.createdAt
                                                    ? moment(entity.createdAt).format('MMM DD, YYYY')
                                                    : 'N/A'
                                                }
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <Dropdown
                                                isOpen={dropdownOpen[entity.id] || false}
                                                toggle={() => toggleDropdown(entity.id)}
                                            >
                                                <DropdownToggle
                                                    tag="button"
                                                    className="btn btn-soft-secondary btn-sm"
                                                    caret={false}
                                                >
                                                    <i className="ri-more-2-fill"></i>
                                                </DropdownToggle>
                                                <DropdownMenu end>
                                                    <DropdownItem onClick={() => handleViewProfile(entity)}>
                                                        <i className="ri-eye-line align-bottom me-2 text-muted"></i>
                                                        View Profile
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Load More Button */}
                {entities.length > 0 && hasMore && (
                    <div className="text-center mt-3">
                        <Button
                            color="soft-primary"
                            size="sm"
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <i className="ri-add-line me-1"></i>
                                    Load More
                                </>
                            )}
                        </Button>
                    </div>
                )}

                {/* Results summary */}
                {!hasMore && totalPages > 1 && (
                    <div className="text-center mt-3">
                        <small className="text-muted">
                            Showing all {entities.length} entities
                            {searchQuery && ` matching "${searchQuery}"`}
                        </small>
                    </div>
                )}
            </CardBody>
        </Card>
    );
};

export default RecentlyAddedEntities;